import {
  type Cell,
  type Column,
  flexRender,
  type Row,
  type RowData,
} from '@tanstack/react-table';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { cn } from 'cn';
import { type CSSProperties, useCallback, useLayoutEffect } from 'react';
import Icon from '../../Icon/Icon';
import type { TableRowSelection, TableVariant } from '../Table';
import type { kkTableFeatures, TableInstance } from '../tableFeatures';
import {
  type TableRowInteractionProps,
  useTableRowInteractions,
} from './useTableRowInteractions';

type VirtualizedRow = Pick<VirtualItem, 'index' | 'start'>;

const virtualizedRowOverscan = 15;
const estimatedVirtualizedRowHeightByVariant = {
  default: 48,
  relaxed: 56,
  compact: 40,
  list: 36,
} satisfies Record<TableVariant, number>;

function getCommonPinningStyles<TData extends RowData>(
  column: Column<typeof kkTableFeatures, TData, unknown>,
): CSSProperties {
  const isPinned = column.getIsPinned();
  return {
    insetInlineStart:
      isPinned === 'start' ? `${column.getStart('start')}px` : undefined,
    insetInlineEnd:
      isPinned === 'end' ? `${column.getAfter('end')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
}

interface TableBodyEventHandlers<TData extends RowData> {
  onRowClick?: (row: Row<typeof kkTableFeatures, TData>) => void;
  onRowMouseEnter?: (row: Row<typeof kkTableFeatures, TData>) => void;
  onRowMouseLeave?: (row: Row<typeof kkTableFeatures, TData>) => void;
}

interface TableBodyRowProps<TData extends RowData>
  extends Omit<TableBodyEventHandlers<TData>, 'onRowClick'> {
  estimatedRowHeight?: number;
  getCellClassName?: (
    cell: Cell<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  getRowClassName?: (row: Row<typeof kkTableFeatures, TData>) => string;
  gridTemplateColumns?: string;
  interactionProps: TableRowInteractionProps;
  row: Row<typeof kkTableFeatures, TData>;
  rowSelection?: TableRowSelection<TData>;
  virtualRow?: VirtualizedRow;
}

function TableBodyRow<TData extends RowData>({
  estimatedRowHeight,
  getCellClassName,
  getRowClassName,
  gridTemplateColumns,
  interactionProps,
  onRowMouseEnter,
  onRowMouseLeave,
  row,
  rowSelection,
  virtualRow,
}: TableBodyRowProps<TData>): React.JSX.Element {
  const isVirtualized = virtualRow !== undefined;
  const rowStyle: CSSProperties | undefined = isVirtualized
    ? {
        display: 'grid',
        gridTemplateColumns,
        minHeight: estimatedRowHeight,
        position: 'absolute',
        transform: `translateY(${virtualRow.start}px)`,
        width: '100%',
      }
    : undefined;

  return (
    <tr
      {...interactionProps}
      data-index={virtualRow?.index}
      onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(row) : undefined}
      onMouseLeave={onRowMouseLeave ? () => onRowMouseLeave(row) : undefined}
      key={row.id}
      role="row"
      style={rowStyle}
      className={cn(
        'group/row content-visibility-auto bg-inherit',
        getRowClassName?.(row),
      )}
    >
      {row.getVisibleCells().map((cell, index) => {
        const cellContent = flexRender(
          cell.column.columnDef.cell,
          cell.getContext(),
        );
        const showSelectionIndicator =
          index === 0 && rowSelection?.showSelectionIndicator;

        return (
          <td
            role={rowSelection ? 'gridcell' : 'cell'}
            data-value={JSON.stringify(cell.getValue())}
            key={cell.id}
            data-testid={`cell-${cell.column.id}`}
            data-column-id={cell.column.id}
            className={cn(
              'group/cell bg-inherit',
              {
                'text-right': cell.column.columnDef.meta?.rightAlign,
                'peer/first-col': index === 0,
              },
              getCellClassName?.(cell),
            )}
            data-is-pinned={cell.column.getIsPinned() ? 'true' : undefined}
            data-is-last-left-pinned-column={
              cell.column.getIsPinned() === 'start' &&
              cell.column.getIsLastColumn('start')
                ? 'true'
                : undefined
            }
            data-is-first-right-pinned-column={
              cell.column.getIsPinned() === 'end' &&
              cell.column.getIsFirstColumn('end')
                ? 'true'
                : undefined
            }
            style={{
              ...(isVirtualized ? {} : getCommonPinningStyles(cell.column)),
            }}
          >
            {showSelectionIndicator ? (
              <div className="flex items-center gap-2">
                {row.getIsSelected() ? (
                  <Icon
                    aria-hidden="true"
                    className="shrink-0 text-blue-100"
                    size="tiny"
                    solid
                    type="check-circle"
                  />
                ) : null}
                {cellContent}
              </div>
            ) : (
              cellContent
            )}
          </td>
        );
      })}
    </tr>
  );
}

interface TableBodyProps<TData extends RowData>
  extends TableBodyEventHandlers<TData> {
  getCellClassName?: (
    cell: Cell<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  getRowClassName?: (row: Row<typeof kkTableFeatures, TData>) => string;
  gridTemplateColumns?: string;
  isVirtualized: boolean;
  rowSelection?: TableRowSelection<TData>;
  table: TableInstance<TData>;
  tableContainerElement: HTMLDivElement | null;
  variant: TableVariant;
}

export function TableBody<TData extends RowData>({
  getCellClassName,
  getRowClassName,
  gridTemplateColumns,
  isVirtualized,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  rowSelection,
  table,
  tableContainerElement,
  variant,
}: TableBodyProps<TData>): React.JSX.Element {
  if (!isVirtualized) {
    return (
      <StaticTableBody<TData>
        getCellClassName={getCellClassName}
        getRowClassName={getRowClassName}
        onRowClick={onRowClick}
        onRowMouseEnter={onRowMouseEnter}
        onRowMouseLeave={onRowMouseLeave}
        rowSelection={rowSelection}
        table={table}
      />
    );
  }

  return (
    <VirtualizedTableBody<TData>
      getCellClassName={getCellClassName}
      getRowClassName={getRowClassName}
      gridTemplateColumns={gridTemplateColumns ?? ''}
      onRowClick={onRowClick}
      onRowMouseEnter={onRowMouseEnter}
      onRowMouseLeave={onRowMouseLeave}
      rowSelection={rowSelection}
      table={table}
      tableContainerElement={tableContainerElement}
      variant={variant}
    />
  );
}

interface StaticTableBodyProps<TData extends RowData>
  extends TableBodyEventHandlers<TData> {
  getCellClassName?: (
    cell: Cell<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  getRowClassName?: (row: Row<typeof kkTableFeatures, TData>) => string;
  rowSelection?: TableRowSelection<TData>;
  table: TableInstance<TData>;
}

function StaticTableBody<TData extends RowData>({
  getCellClassName,
  getRowClassName,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  rowSelection,
  table,
}: StaticTableBodyProps<TData>): React.JSX.Element {
  const rows = table.getRowModel().rows;
  const { getRowInteractionProps } = useTableRowInteractions({
    onRowClick,
    rowSelection,
    rows,
  });

  return (
    <tbody className="bg-inherit">
      {rows.map((row, index) => (
        <TableBodyRow<TData>
          getCellClassName={getCellClassName}
          getRowClassName={getRowClassName}
          interactionProps={getRowInteractionProps(row, index)}
          key={row.id}
          onRowMouseEnter={onRowMouseEnter}
          onRowMouseLeave={onRowMouseLeave}
          row={row}
          rowSelection={rowSelection}
        />
      ))}
    </tbody>
  );
}

interface VirtualizedTableBodyProps<TData extends RowData>
  extends TableBodyEventHandlers<TData> {
  getCellClassName?: (
    cell: Cell<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  getRowClassName?: (row: Row<typeof kkTableFeatures, TData>) => string;
  gridTemplateColumns: string;
  rowSelection?: TableRowSelection<TData>;
  table: TableInstance<TData>;
  tableContainerElement: HTMLDivElement | null;
  variant: TableVariant;
}

function VirtualizedTableBody<TData extends RowData>({
  getCellClassName,
  getRowClassName,
  gridTemplateColumns,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  rowSelection,
  table,
  tableContainerElement,
  variant,
}: VirtualizedTableBodyProps<TData>): React.JSX.Element {
  const rows = table.getRowModel().rows;
  const estimatedRowHeight = estimatedVirtualizedRowHeightByVariant[variant];
  const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: rows.length,
    estimateSize: () => estimatedRowHeight,
    getScrollElement: () => tableContainerElement,
    initialRect: {
      height: estimatedRowHeight * 10,
      width: 0,
    },
    measureElement:
      typeof window !== 'undefined' &&
      !window.navigator.userAgent.includes('Firefox')
        ? (element) => element.getBoundingClientRect().height
        : undefined,
    overscan: virtualizedRowOverscan,
  });
  const scrollToIndex = useCallback(
    (index: number) => rowVirtualizer.scrollToIndex(index, { align: 'auto' }),
    [rowVirtualizer],
  );
  const { getRowInteractionProps } = useTableRowInteractions({
    measureElement: rowVirtualizer.measureElement,
    onRowClick,
    rowSelection,
    rows,
    scrollToIndex,
  });

  useLayoutEffect(() => {
    if (!tableContainerElement) return;
    rowVirtualizer.measure();
    const frame = window.requestAnimationFrame(() => rowVirtualizer.measure());
    return () => window.cancelAnimationFrame(frame);
  }, [gridTemplateColumns, rowVirtualizer, rows.length, tableContainerElement]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const rowsToRender: VirtualizedRow[] =
    virtualRows.length > 0
      ? virtualRows
      : rows.slice(0, virtualizedRowOverscan).map((_, index) => ({
          index,
          start: index * estimatedRowHeight,
        }));
  return (
    <tbody
      className="bg-inherit"
      data-virtualized="true"
      style={{
        display: 'grid',
        height: `${rowVirtualizer.getTotalSize()}px`,
        position: 'relative',
        width: '100%',
      }}
    >
      {rowsToRender.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) return null;

        return (
          <TableBodyRow<TData>
            getCellClassName={getCellClassName}
            getRowClassName={getRowClassName}
            estimatedRowHeight={estimatedRowHeight}
            gridTemplateColumns={gridTemplateColumns}
            interactionProps={getRowInteractionProps(row, virtualRow.index)}
            key={row.id}
            onRowMouseEnter={onRowMouseEnter}
            onRowMouseLeave={onRowMouseLeave}
            row={row}
            rowSelection={rowSelection}
            virtualRow={virtualRow}
          />
        );
      })}
    </tbody>
  );
}
